import { useEffect } from 'react';
import { tabbable } from 'tabbable';

interface Options {
  disable: boolean;
}

/**
 * Scopes the tab order to the React element tree, instead of the DOM tree.This way, when the user navigates
 * with tab from the triggerRef, the tab order moves into the popup, and then out of the popup back to the
 * rest of the document.
 * @param trigger Either the ref that is attached to the trigger element or the trigger element itself
 * @param popover Either the ref that is attached to the popover element or the popover element itself
 * @param options Allows us to disable messing with the tab order when the popover is not visible
 */
export default function useSimulateTabNavigationForReactTree<T extends HTMLElement, P extends HTMLElement>(
  trigger?: React.RefObject<T> | HTMLElement,
  popover?: React.RefObject<P> | HTMLElement,
  options: Options = { disable: false }
): void {
  const triggerNode = trigger && 'current' in trigger ? trigger?.current : trigger;
  const popoverNode = popover && 'current' in popover ? popover?.current : popover;

  const ownerDocument = triggerNode?.ownerDocument || document;

  function handleKeyDown(event: KeyboardEvent) {
    if (event.key === 'Tab' && popoverNode && tabbable(popoverNode).length === 0) {
      return;
    }

    if (event.key === 'Tab' && event.shiftKey) {
      if (shiftTabbedFromElementAfterTrigger(event)) {
        focusLastTabbableInPopover(event);
      } else if (shiftTabbedOutOfPopover(event)) {
        focusTriggerRef(event);
      } else if (shiftTabbedToBrowserChrome(event)) {
        disableTabbablesInPopover();
      }
    } else if (event.key === 'Tab') {
      if (tabbedFromTriggerToPopover()) {
        focusFirstPopoverTabbable(event);
      } else if (tabbedOutOfPopover()) {
        focusTabbableAfterTrigger(event);
      } else if (tabbedToBrowserChrome(event)) {
        disableTabbablesInPopover();
      }
    }
  }

  useEffect(() => {
    if (options.disable || !triggerNode || !popoverNode) return;

    ownerDocument.addEventListener('keydown', handleKeyDown);
    return () => {
      ownerDocument.removeEventListener('keydown', handleKeyDown);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options.disable, triggerNode, popoverNode]);

  function getElementAfterTrigger() {
    const elements = tabbable(ownerDocument.documentElement);
    const targetIndex = elements && triggerNode ? elements.indexOf(triggerNode) : -1;
    const elementAfterTrigger = elements?.[targetIndex + 1];
    return popoverNode?.contains(elementAfterTrigger || null) ? false : elementAfterTrigger;
  }

  function tabbedFromTriggerToPopover() {
    return triggerNode ? triggerNode === ownerDocument.activeElement : false;
  }

  function focusFirstPopoverTabbable(event: KeyboardEvent) {
    const elements = popoverNode && tabbable(popoverNode);
    if (elements?.[0]) {
      event.preventDefault();
      elements[0].focus();
    }
  }

  function tabbedOutOfPopover() {
    const inPopover = popoverNode?.contains(ownerDocument.activeElement || null) ?? false;

    if (inPopover) {
      const elements = popoverNode && tabbable(popoverNode);
      return Boolean(elements?.[elements.length - 1] === ownerDocument.activeElement);
    }
    return false;
  }

  function focusTabbableAfterTrigger(event: KeyboardEvent) {
    const elementAfterTrigger = getElementAfterTrigger();
    if (elementAfterTrigger) {
      event.preventDefault();
      elementAfterTrigger.focus();
    }
  }

  function shiftTabbedFromElementAfterTrigger(event: KeyboardEvent) {
    if (!event.shiftKey) return;
    const elementAfterTrigger = getElementAfterTrigger();
    return event.target === elementAfterTrigger;
  }

  function focusLastTabbableInPopover(event: KeyboardEvent) {
    const elements = popoverNode && tabbable(popoverNode);
    const last = elements?.[elements.length - 1];
    if (last) {
      event.preventDefault();
      last.focus();
    }
  }

  function shiftTabbedOutOfPopover(event: KeyboardEvent) {
    const elements = popoverNode && tabbable(popoverNode);
    if (elements) {
      return elements.length === 0 ? false : event.target === elements[0];
    }
    return false;
  }

  function focusTriggerRef(event: KeyboardEvent) {
    event.preventDefault();
    triggerNode?.focus();
  }

  function tabbedToBrowserChrome(event: KeyboardEvent) {
    const allTabbableElements = tabbable(ownerDocument.documentElement);

    const elements = popoverNode && allTabbableElements.filter((element) => !popoverNode.contains(element));
    return elements ? event.target === elements[elements.length - 1] : false;
  }

  function shiftTabbedToBrowserChrome(event: KeyboardEvent) {
    // we're assuming the popover will never contain the first tabbable
    // element, and it better not, because the trigger needs to be tabbable!
    return event.target === tabbable(ownerDocument.documentElement)[0];
  }

  const restoreTabIndexTuples: [HTMLElement | SVGElement, number][] = [];

  function disableTabbablesInPopover() {
    const elements = popoverNode && tabbable(popoverNode);
    if (elements) {
      elements.forEach((element) => {
        restoreTabIndexTuples.push([element, element.tabIndex]);
        element.tabIndex = -1;
      });
      ownerDocument.addEventListener('focusin', enableTabbablesInPopover);
    }
  }

  function enableTabbablesInPopover() {
    ownerDocument.removeEventListener('focusin', enableTabbablesInPopover);
    restoreTabIndexTuples.forEach(([element, tabIndex]) => {
      element.tabIndex = tabIndex;
    });
  }
}
